import {t} from 'i18next';
import {IconButton, Separator} from 'platform/components';
import {Box, Device, Hide, HStack, Show, Space, useDevice, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {UseDocumentPreviewReturnType} from '../types/UseDocumentPreviewReturnType';
import {DocumentPreviewSlideshow} from './DocumentPreviewSlideshow';
import {DocumentPreviewThumbnails} from './DocumentPreviewThumbnails';

interface DocumentPreviewProps {
  documentPreview: UseDocumentPreviewReturnType;
}

export function DocumentPreview(props: DocumentPreviewProps) {
  const device = useDevice();

  const isCollapsed = props.documentPreview.state.isCollapsed;

  const handleCollapse = () => {
    props.documentPreview.controls.toggleCollapsed();
  };

  return (
    <HStack
      height="100%"
      width={isCollapsed ? 'auto' : '100%'}
      minWidth={isCollapsed ? 'auto' : '40%'}
    >
      <StyledCollapsibleSidebar $isCollapsed={isCollapsed} $device={device}>
        <Show when={isCollapsed}>
          <HStack align="flex-start" justify="center" width="100%" height="100%">
            <StyledRotatedText>
              {t('entity.documentPreview.labels.documentPreview')}
            </StyledRotatedText>
          </HStack>
        </Show>

        <Show onMobile onTablet>
          <Box flexGrow={1} />
        </Show>

        <Hide when={isCollapsed} onMobile onTablet>
          <DocumentPreviewThumbnails documentPreview={props.documentPreview} />
        </Hide>

        <VStack shrink={0} width="100%" align="center">
          <Space fillAvailable />
          <Separator spacing={2} />
          <IconButton
            icon={isCollapsed ? 'action/collapse_open' : 'action/collapse_close'}
            onClick={handleCollapse}
          />
        </VStack>
      </StyledCollapsibleSidebar>

      <Hide when={isCollapsed}>
        <Box height="100%" width="100%" backgroundColor="palettes.neutral.30.100">
          <DocumentPreviewSlideshow documentPreview={props.documentPreview} />
        </Box>
      </Hide>
    </HStack>
  );
}

const StyledCollapsibleSidebar = styled.div<{
  $isCollapsed: boolean;
  $device: Device;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${({$isCollapsed, theme}) => ($isCollapsed ? theme.getSize(10) : theme.getSize(25))};
  padding-top: ${({$isCollapsed, theme}) => ($isCollapsed ? 0 : theme.getSize(4))};
  padding-left: ${({$isCollapsed, theme}) => ($isCollapsed ? 0 : theme.getSize(4))};
  padding-bottom: ${({theme}) => theme.getSize(2)};
  background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};

  ${(props) =>
    props.$device === 'mobile' || props.$device === 'tablet'
      ? `width: ${props.theme.getSize(10)}; padding-top: 0; padding-left: 0;`
      : ''}
`;

// We need to rotate the text + set it's font size
// eslint-disable-next-line eag/no-css-property
const StyledRotatedText = styled.div`
  position: absolute;
  top: ${({theme}) => theme.getSize(TOP_PADDING)};
  transform: rotate(-90deg);
  font-size: ${({theme}) => theme.fontSizes.text.xSmall};
  white-space: nowrap;
`;

const TOP_PADDING = 15;
